import Dexie from 'dexie'

export const db = new Dexie('rutes-hado')

// Versió 1 — estructura original (conservada per migració)
db.version(1).stores({
  slots:      '++id, rutaId, data, horaInici, horaFi, estat, createdAt',
  rutes:      '++id, nom, tipus, distanciaKm, duracioMin, estat, isFavorit, createdAt, updatedAt',
  anotacions: '++id, rutaId, slotId, text, lat, lng, tipus, createdAt',
})

// Versió 2 — esquema intermedi (conservada per migració)
db.version(2).stores({
  slots:      '++id, codi',
  rutes:      '++id, nom, data_creacio',
  anotacions: '++id, ruta_id, slot_id',
}).upgrade(async tx => {
  await tx.table('slots').clear()
  await tx.table('rutes').clear()
  await tx.table('anotacions').clear()
})

// Versió 3 — esquema definitiu: slots→domicilis, afegeix config i data a rutes
db.version(3).stores({
  slots:      null,                             // elimina la taula antiga
  domicilis:  '++id, codi',
  rutes:      '++id, nom, data_creacio, data',  // data = dia de la ruta (YYYY-MM-DD)
  anotacions: '++id, ruta_id, domicili_id',     // relació ruta ↔ domicili (amb ordre)
  config:     'id',                             // singleton: id sempre = 1
}).upgrade(async tx => {
  // Neteja dades v2 incompatibles
  await tx.table('anotacions').clear()
  await tx.table('rutes').clear()
  // Inicialitza config per defecte
  await tx.table('config').put({ id: 1, hora_inici: '09:00', tipus_ruta: 'peu' })
})

// En base de dades nova (sense versió anterior), seed config
db.on('populate', async () => {
  await db.config.add({ id: 1, hora_inici: '09:00', tipus_ruta: 'peu' })
})

// ── CRUD Domicilis ─────────────────────────────────────────────────────────────
// Estructura: { id, codi, adreça, dades_json: { lat, lng, notes, visitat } }

export const domicilisDB = {
  getAll:    ()         => db.domicilis.orderBy('codi').toArray(),
  getByCodi: (codi)     => db.domicilis.where('codi').equals(codi).first(),
  getById:   (id)       => db.domicilis.get(id),
  getByIds:  (ids)      => db.domicilis.where('id').anyOf(ids).toArray(),
  create:    (data)     => db.domicilis.add(data),
  update:    (id, data) => db.domicilis.update(id, data),
  delete:    (id)       =>
    db.transaction('rw', db.domicilis, db.anotacions, async () => {
      await db.anotacions.where('domicili_id').equals(id).delete()
      await db.domicilis.delete(id)
    }),
}

// ── CRUD Rutes ─────────────────────────────────────────────────────────────────
// Estructura: { id, nom, data_creacio, data, tipus, hora_inici, restriccions }

export const rutesDB = {
  getAll:   ()         => db.rutes.orderBy('data').reverse().toArray(),
  getById:  (id)       => db.rutes.get(id),
  create:   (data)     => db.rutes.add({ ...data, data_creacio: new Date().toISOString() }),
  update:   (id, data) => db.rutes.update(id, data),
  delete:   (id)       =>
    db.transaction('rw', db.rutes, db.anotacions, async () => {
      await db.anotacions.where('ruta_id').equals(id).delete()
      await db.rutes.delete(id)
    }),
}

// ── CRUD Anotacions (relació ruta ↔ domicili, amb ordre i restriccions) ────────
// Estructura: { id, ruta_id, domicili_id, ordre, restriccions }

export const anotacionsDB = {
  getByRuta: (ruta_id) =>
    db.anotacions.where('ruta_id').equals(ruta_id).toArray()
      .then(anots => anots.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))),
  getByDomicili: (domicili_id) =>
    db.anotacions.where('domicili_id').equals(domicili_id).toArray(),
  create:       (data)    => db.anotacions.add(data),
  update:       (id, data) => db.anotacions.update(id, data),
  delete:       (id)      => db.anotacions.delete(id),
  deleteByRuta: (ruta_id) => db.anotacions.where('ruta_id').equals(ruta_id).delete(),
}

// ── Config (singleton) ─────────────────────────────────────────────────────────
// Estructura: { id:1, hora_inici, tipus_ruta, ubicacio_inici, hospital }

const CONFIG_DEFAULT = {
  id:          1,
  hora_inici:  '09:00',
  tipus_ruta:  'peu',
  num_slots:   40,
  temps_atencio: {
    molt_baixa: 15,
    baixa:      20,
    normal:     30,
    alta:       45,
    molt_alta:  60,
  },
  temps_ingres: 20,
  temps_alta:   15,
  ubicacio_inici: { tipus: 'casa', adreca: '', lat: null, lng: null },
  hospital:       { nom: '', adreca: '', lat: null, lng: null },
}

export const configDB = {
  get:          ()     => db.config.get(1).then(c => c ?? CONFIG_DEFAULT),
  update:       (data) => db.config.put({ id: 1, ...data }),
  getOrDefault: ()     => db.config.get(1).then(c => ({ ...CONFIG_DEFAULT, ...c })),
}
